import { safeLocalStorage } from "./safeLocalStorage.ts";

export const MOBILE_P3_SHORTCUT_STORAGE_KEY = "bbgmMobileP3Shortcuts";
export const MOBILE_P3_RECENT_STORAGE_KEY = "bbgmMobileP3RecentItems";
export const MAX_MOBILE_P3_SHORTCUTS = 7;
export const MAX_MOBILE_P3_RECENT_ITEMS = 5;
export const MOBILE_P3_REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

export type MobileP3ShortcutKind = "data" | "marketFilter" | "player" | "team";
export type MobileP3RecentKind = MobileP3ShortcutKind | "draft" | "game" | "seasonStory";

export type MobileP3Shortcut = {
	context?: string;
	href: string;
	id: string;
	kind: MobileP3ShortcutKind;
	label: string;
	pinnedAt?: number;
};

export type MobileP3ShortcutInput = Partial<MobileP3Shortcut>;

export type MobileP3RecentItem = {
	context?: string;
	href: string;
	id: string;
	kind: MobileP3RecentKind;
	label: string;
	viewedAt?: number;
};

export type MobileP3RecentItemInput = Partial<MobileP3RecentItem>;

export type MobileP3Storage = Pick<
	typeof safeLocalStorage,
	"getItem" | "removeItem" | "setItem"
>;

export type MobileP3NativeCapabilities = {
	clipboard: boolean;
	download: boolean;
	haptic: boolean;
	nativeShare: boolean;
};

export type MobileP3NavigatorLike = {
	clipboard?: {
		writeText?: (text: string) => Promise<void> | void;
	};
	share?: (data: ShareData) => Promise<void> | void;
	userActivation?: {
		hasBeenActive: boolean;
	};
	vibrate?: (pattern: number | number[]) => boolean;
};

export type MobileP3DocumentLike = {
	createElement?: Document["createElement"];
};

export type MobileP3ShareMode = "clipboard" | "download" | "nativeShare" | "textOnly";

export type MobileP3SharePayload = {
	text: string;
	title: string;
	url?: string;
};

// R7-2/R7-3: the settings-save kind had no mount (the settings page was
// removed) and the news-filter kind was retired (information browsing gets no
// emotional feedback, GAME_PRODUCT_DESIGN_REVIEW D4). Both are dropped from the
// union, leaving only the three kinds that still have real mounts.
export type MobileP3EventKind = "draftReveal" | "liveKeyPlay" | "tradeAccepted";

export type MobileP3FeedbackPlan = {
	animationEnabled: boolean;
	event: MobileP3EventKind;
	hapticPattern?: number[];
	statusText: string;
};

const mobileP3ShortcutKinds = new Set<MobileP3ShortcutKind>([
	"data",
	"marketFilter",
	"player",
	"team",
]);

const mobileP3RecentKinds = new Set<MobileP3RecentKind>([
	"data",
	"draft",
	"game",
	"marketFilter",
	"player",
	"seasonStory",
	"team",
]);

const eventFeedback: Record<
	MobileP3EventKind,
	{ hapticPattern: number[]; statusText: string }
> = {
	draftReveal: {
		hapticPattern: [12, 24, 12],
		statusText: "Draft reveal ready",
	},
	liveKeyPlay: {
		hapticPattern: [16],
		statusText: "Key play highlighted",
	},
	tradeAccepted: {
		hapticPattern: [20, 20, 20],
		statusText: "Trade accepted",
	},
};

const cleanText = (value: unknown, maxLength: number) => {
	if (typeof value !== "string") {
		return undefined;
	}

	const trimmed = value.trim().replace(/\s+/g, " ");
	return trimmed ? trimmed.slice(0, maxLength) : undefined;
};

const cleanTimestamp = (value: unknown) =>
	typeof value === "number" && Number.isFinite(value) && value > 0
		? Math.round(value)
		: undefined;

export const getMobileP3ShortcutKey = ({
	id,
	kind,
}: Pick<MobileP3Shortcut, "id" | "kind">) => `${kind}:${id}`;

export const getMobileP3RecentKey = ({
	id,
	kind,
}: Pick<MobileP3RecentItem, "id" | "kind">) => `${kind}:${id}`;

export const normalizeMobileP3Shortcuts = (
	shortcuts: readonly MobileP3ShortcutInput[],
	limit = MAX_MOBILE_P3_SHORTCUTS,
) => {
	const normalized: MobileP3Shortcut[] = [];
	const seen = new Set<string>();
	const safeLimit = Math.max(0, Math.min(MAX_MOBILE_P3_SHORTCUTS, Math.floor(limit)));

	for (const shortcut of shortcuts) {
		const id = cleanText(shortcut.id, 80);
		const label = cleanText(shortcut.label, 80);
		const href = cleanText(shortcut.href, 240);
		const kind = shortcut.kind;

		if (!id || !label || !href || !mobileP3ShortcutKinds.has(kind as MobileP3ShortcutKind)) {
			continue;
		}

		const nextShortcut: MobileP3Shortcut = {
			href,
			id,
			kind: kind as MobileP3ShortcutKind,
			label,
		};
		const context = cleanText(shortcut.context, 120);
		if (context) {
			nextShortcut.context = context;
		}

		const pinnedAt = cleanTimestamp(shortcut.pinnedAt);
		if (pinnedAt !== undefined) {
			nextShortcut.pinnedAt = pinnedAt;
		}

		const shortcutKey = getMobileP3ShortcutKey(nextShortcut);
		if (seen.has(shortcutKey)) {
			continue;
		}

		seen.add(shortcutKey);
		normalized.push(nextShortcut);

		if (normalized.length >= safeLimit) {
			break;
		}
	}

	return normalized;
};

export const loadMobileP3Shortcuts = (storage: MobileP3Storage = safeLocalStorage) => {
	const raw = storage.getItem(MOBILE_P3_SHORTCUT_STORAGE_KEY);
	if (!raw) {
		return [];
	}

	try {
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? normalizeMobileP3Shortcuts(parsed) : [];
	} catch {
		return [];
	}
};

export const saveMobileP3Shortcuts = (
	shortcuts: readonly MobileP3ShortcutInput[],
	storage: MobileP3Storage = safeLocalStorage,
) => {
	const normalized = normalizeMobileP3Shortcuts(shortcuts);

	if (normalized.length === 0) {
		storage.removeItem(MOBILE_P3_SHORTCUT_STORAGE_KEY);
		return normalized;
	}

	storage.setItem(MOBILE_P3_SHORTCUT_STORAGE_KEY, JSON.stringify(normalized));
	return normalized;
};

export const normalizeMobileP3RecentItems = (
	items: readonly MobileP3RecentItemInput[],
	limit = MAX_MOBILE_P3_RECENT_ITEMS,
) => {
	const normalized: MobileP3RecentItem[] = [];
	const seen = new Set<string>();
	const safeLimit = Math.max(0, Math.min(MAX_MOBILE_P3_RECENT_ITEMS, Math.floor(limit)));

	for (const item of items) {
		const id = cleanText(item.id, 80);
		const label = cleanText(item.label, 80);
		const href = cleanText(item.href, 240);
		const kind = item.kind;

		if (!id || !label || !href || !mobileP3RecentKinds.has(kind as MobileP3RecentKind)) {
			continue;
		}

		const nextItem: MobileP3RecentItem = {
			href,
			id,
			kind: kind as MobileP3RecentKind,
			label,
		};
		const context = cleanText(item.context, 120);
		if (context) {
			nextItem.context = context;
		}

		const viewedAt = cleanTimestamp(item.viewedAt);
		if (viewedAt !== undefined) {
			nextItem.viewedAt = viewedAt;
		}

		const recentKey = getMobileP3RecentKey(nextItem);
		if (seen.has(recentKey)) {
			continue;
		}

		seen.add(recentKey);
		normalized.push(nextItem);

		if (normalized.length >= safeLimit) {
			break;
		}
	}

	return normalized;
};

export const loadMobileP3RecentItems = (storage: MobileP3Storage = safeLocalStorage) => {
	const raw = storage.getItem(MOBILE_P3_RECENT_STORAGE_KEY);
	if (!raw) {
		return [];
	}

	try {
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? normalizeMobileP3RecentItems(parsed) : [];
	} catch {
		return [];
	}
};

export const saveMobileP3RecentItems = (
	items: readonly MobileP3RecentItemInput[],
	storage: MobileP3Storage = safeLocalStorage,
) => {
	const normalized = normalizeMobileP3RecentItems(items);

	if (normalized.length === 0) {
		storage.removeItem(MOBILE_P3_RECENT_STORAGE_KEY);
		return normalized;
	}

	storage.setItem(MOBILE_P3_RECENT_STORAGE_KEY, JSON.stringify(normalized));
	return normalized;
};

export const getMobileP3NativeCapabilities = ({
	documentLike,
	navigatorLike,
}: {
	documentLike?: MobileP3DocumentLike;
	navigatorLike?: MobileP3NavigatorLike;
} = {}): MobileP3NativeCapabilities => {
	const resolvedNavigator =
		navigatorLike ?? (typeof navigator === "undefined" ? undefined : navigator);
	const resolvedDocument =
		documentLike ?? (typeof document === "undefined" ? undefined : document);

	return {
		clipboard: typeof resolvedNavigator?.clipboard?.writeText === "function",
		download: typeof resolvedDocument?.createElement === "function",
		haptic: typeof resolvedNavigator?.vibrate === "function",
		nativeShare: typeof resolvedNavigator?.share === "function",
	};
};

export const chooseMobileP3ShareMode = ({
	clipboard,
	download,
	nativeShare,
}: Pick<MobileP3NativeCapabilities, "clipboard" | "download" | "nativeShare">): MobileP3ShareMode => {
	if (nativeShare) {
		return "nativeShare";
	}
	if (clipboard) {
		return "clipboard";
	}
	if (download) {
		return "download";
	}
	return "textOnly";
};

export const createMobileP3ShareText = ({
	text,
	title,
	url,
}: MobileP3SharePayload) => [title, text, url].filter(Boolean).join("\n");

export const createMobileP3ExportFilename = ({
	extension = "csv",
	label,
	season,
}: {
	extension?: string;
	label: string;
	season?: number;
}) => {
	const cleanLabel =
		cleanText(label, 80)
			?.toLowerCase()
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/^-|-$/g, "") ?? "mobile-p3-export";
	const cleanExtension =
		cleanText(extension, 12)
			?.toLowerCase()
			.replace(/[^a-z0-9]/g, "") || "csv";
	const cleanSeason =
		typeof season === "number" && Number.isFinite(season) ? `-${Math.round(season)}` : "";

	return `${cleanLabel}${cleanSeason}.${cleanExtension}`;
};

export const prefersMobileP3ReducedMotion = (
	matchMedia: Window["matchMedia"] | undefined =
		typeof window === "undefined" || typeof window.matchMedia !== "function"
			? undefined
			: window.matchMedia.bind(window),
) => {
	if (!matchMedia) {
		return false;
	}

	return matchMedia(MOBILE_P3_REDUCED_MOTION_QUERY).matches;
};

export const getMobileP3FeedbackPlan = ({
	capabilities = getMobileP3NativeCapabilities(),
	event,
	reducedMotion = prefersMobileP3ReducedMotion(),
}: {
	capabilities?: Pick<MobileP3NativeCapabilities, "haptic">;
	event: MobileP3EventKind;
	reducedMotion?: boolean;
}): MobileP3FeedbackPlan => {
	const feedback = eventFeedback[event];
	return {
		animationEnabled: !reducedMotion,
		event,
		hapticPattern: capabilities.haptic ? feedback.hapticPattern : undefined,
		statusText: feedback.statusText,
	};
};

export const triggerMobileP3Haptic = (
	navigatorLike: Pick<MobileP3NavigatorLike, "userActivation" | "vibrate"> | undefined,
	pattern: number | number[] | undefined,
) => {
	// Chromium requires a real interaction in this frame before vibration.
	// Sticky activation preserves haptics after genuine user actions while
	// preventing mount-time feedback from issuing a blocked API call.
	if (
		!pattern ||
		navigatorLike?.userActivation?.hasBeenActive !== true ||
		typeof navigatorLike.vibrate !== "function"
	) {
		return false;
	}

	return navigatorLike.vibrate(pattern);
};
